(() => {
  if (window.J2VehicleConfiguratorLoaded) return;
  window.J2VehicleConfiguratorLoaded = true;

  class J2VehicleConfigurator {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.formId = root.dataset.formId;
      this.data = this.getData();
      this.steps = Array.isArray(this.data.steps) ? this.data.steps : [];
      this.selected = new Map();
      this.stepIndex = 0;

      // Move modal root to body so it is not trapped under product-page stacking contexts.
      if (this.root.parentElement !== document.body) {
        document.body.appendChild(this.root);
      }

      this.render();
      this.bind();
      this.applyDefaultSelections();
      this.goToStep(0);
      this.resetVariantControls();
      this.applyIncludedDuplicateRules();
      this.updateSelected();
    }

    getData() {
      const script = document.getElementById(`J2ConfiguratorData-${this.sectionId}`);

      if (!script) return {};

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('J2 configurator: invalid JSON data', error);
        return {};
      }
    }

    get form() {
      if (!this.formId) return null;

      return document.getElementById(this.formId);
    }

    get mainVariantId() {
      const input = this.form?.querySelector('[name="id"]');
      const value = input?.value;

      return value ? Number(value) : null;
    }

    get quantity() {
      const input = this.form?.querySelector('[name="quantity"]');
      const value = input?.value;

      return Math.max(1, Number(value || 1));
    }

    render() {
      this.root.hidden = false;

      if (!this.steps.length) {
        this.root.innerHTML = '';
        return;
      }

      const progress = this.steps.map((step, index) => `
        <button type="button" class="j2-configurator-progress__item${index === 0 ? ' is-active' : ''}" data-progress-step="${index}">
           <span>Stap ${index + 1} van ${this.steps.length}</span>
          <strong>${this.escape(step.title || '')}</strong>
        </button>
      `).join('');

      const mobileStepOptions = this.steps.map((step, index) => `
        <option value="${index}">Stap ${index + 1} van ${this.steps.length}: ${this.escape(step.title || '')}</option>
      `).join('');

      const stepMarkup = this.steps.map((step, stepIndex) => {
        const items = Array.isArray(step.items) ? step.items : [];

        const itemsMarkup = items.length
          ? items.map((item, itemIndex) => this.optionMarkup(item, step, stepIndex, itemIndex)).join('')
          : `<p class="j2-configurator-empty">${this.escape(step.emptyText || 'Geen opties beschikbaar.')}</p>`;

        // <p class="j2-configurator-eyebrow">Stap ${stepIndex + 1} van ${this.steps.length}</p>
        // <h3 class="j2-configurator-step__title">${this.escape(step.title || '')}</h3>
        return `
          <section class="j2-configurator-step" data-step="${stepIndex}" ${stepIndex === 0 ? '' : 'hidden'}>
            <div class="j2-configurator-step__header">
              ${
                step.mode === 'single'
                  ? '<p class="j2-configurator-step__help">Kies maximaal één optie.</p>'
                  : '<p class="j2-configurator-step__help">Kies één of meerdere opties.</p>'
              }
            </div>

            <div class="j2-configurator-grid">
              ${itemsMarkup}
            </div>
          </section>
        `;
      }).join('');

      this.root.innerHTML = `
        <div class="j2-configurator-modal" data-modal hidden>
          <div class="j2-configurator-modal__overlay" data-close></div>

          <div class="j2-configurator-modal__panel" role="dialog" aria-modal="true" aria-label="Voertuig configureren">
            <button type="button" class="j2-configurator-close" data-close aria-label="Sluiten">×</button>

            <header class="j2-configurator-modal__top">
              <div>
                <h2 class="j2-configurator-modal__title">Configureer jouw voertuig</h2>
                <p class="j2-configurator-modal__subtitle">${this.escape(this.data.productTitle || '')}</p>
              </div>
            </header>

            <div class="j2-configurator-progress" aria-label="Configuratie stappen">
              ${progress}
            </div>

            <div class="j2-configurator-mobile-nav">
              <select id="j2-step-select-${this.sectionId}" data-step-select>
                ${mobileStepOptions}
              </select>
            </div>

            <div class="j2-configurator-body">
              ${stepMarkup}
            </div>

            <footer class="j2-configurator-footer">
              <div class="j2-configurator-summary">
                <span><span data-selected-count>0</span> opties geselecteerd</span>
                <strong>Extra opties: <span data-selected-total>${this.money(0)}</span></strong>
              </div>

              <div class="j2-configurator-actions">
                <button type="button" class="btn btn--secondary" data-prev>Vorige</button>
                <button type="button" class="btn btn--primary" data-next>Volgende</button>
                <button type="button" class="btn btn--primary" data-submit hidden>Toevoegen</button>
              </div>
            </footer>
          </div>

          <div class="j2-configurator-image-zoom" data-image-zoom-modal hidden>
            <button
              type="button"
              class="j2-configurator-image-zoom__overlay"
              data-image-zoom-close
              aria-label="Vergrote afbeelding sluiten"
            ></button>

            <div
              class="j2-configurator-image-zoom__panel"
              role="dialog"
              aria-modal="true"
              aria-label="Vergrote productafbeelding"
            >
              <button
                type="button"
                class="j2-configurator-close j2-configurator-image-zoom__close"
                data-image-zoom-close
                aria-label="Sluiten"
              >×</button>

              <img
                class="j2-configurator-image-zoom__image"
                data-image-zoom-img
                alt=""
              >
            </div>
          </div>
        </div>
      `;
    }

    optionMarkup(item, step, stepIndex, itemIndex) {
      const inputType = step.mode === 'single' ? 'radio' : 'checkbox';
      const inputId = `j2-option-${this.sectionId}-${stepIndex}-${itemIndex}`;
      const inputName = `j2-configurator-${this.sectionId}-${stepIndex}`;

      const image = item.image
        ? `
          <div class="j2-configurator-option__image">
            <img src="${this.escapeAttr(item.image)}" alt="${this.escapeAttr(item.label || '')}" loading="lazy" data-option-img>

            <button
              type="button"
              class="j2-configurator-option__zoom"
              data-image-zoom
              aria-label="Bekijk ${this.escapeAttr(item.label || 'afbeelding')} groter"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" stroke-width="2"></circle>
                <path d="M16 16L21 21" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
              </svg>
            </button>
          </div>
        `
        : `<div class="j2-configurator-option__image j2-configurator-option__image--placeholder"></div>`;

      return `
        <div class="j2-configurator-option" data-option-card>
          <input
            id="${inputId}"
            type="${inputType}"
            name="${inputName}"
            data-option-input
            data-step-index="${stepIndex}"
            data-item-index="${itemIndex}"
          >

          <div class="j2-configurator-option__inner" data-card-select>
            ${image}

            <div class="j2-configurator-option__content">
              <span class="j2-configurator-option__label">${this.escape(item.label || '')}</span>

              ${
                item.description
                  ? `<span class="j2-configurator-option__description">${this.escape(item.description)}</span>`
                  : ''
              }

              <span class="j2-configurator-option__rule" data-rule-message hidden></span>

              ${this.variantControlsMarkup(item, stepIndex, itemIndex)}

              <span class="j2-configurator-option__price" data-option-price>
                ${this.priceMarkup(item)}
              </span>
            </div>

            <span class="j2-configurator-option__check" aria-hidden="true"></span>
          </div>
        </div>
      `;
    }

    bind() {
      this.root.addEventListener('click', (event) => {
        const imageZoomClose = event.target.closest('[data-image-zoom-close]');
        if (imageZoomClose) {
          event.preventDefault();
          event.stopPropagation();
          this.closeImageZoom();
          return;
        }

        const imageZoomButton = event.target.closest('[data-image-zoom]');
        if (imageZoomButton) {
          event.preventDefault();
          event.stopPropagation();
          this.openImageZoom(imageZoomButton);
          return;
        }
        
        const closeButton = event.target.closest('[data-close]');
        if (closeButton) {
          event.preventDefault();
          this.close();
          return;
        }

        const progressButton = event.target.closest('[data-progress-step]');
        if (progressButton) {
          event.preventDefault();
          this.goToStep(Number(progressButton.dataset.progressStep));
          return;
        }

        const prevButton = event.target.closest('[data-prev]');
        if (prevButton) {
          event.preventDefault();
          this.goToStep(this.stepIndex - 1);
          return;
        }

        const nextButton = event.target.closest('[data-next]');
        if (nextButton) {
          event.preventDefault();
          this.goToStep(this.stepIndex + 1);
          return;
        }

        const submitButton = event.target.closest('[data-submit]');
        if (submitButton) {
          event.preventDefault();
          this.submit();
          return;
        }

        const swatch = event.target.closest('button[data-variant-option]');
        if (swatch) {
          event.preventDefault();
          event.stopPropagation();

          const card = swatch.closest('[data-option-card]');
          const input = card ? card.querySelector('[data-option-input]') : null;
          const optionIndex = swatch.dataset.optionIndex;

          if (input) {
            input.checked = true;
          }

          card.querySelectorAll(`button[data-variant-option][data-option-index="${optionIndex}"]`).forEach((button) => {
            button.classList.toggle('is-active', button === swatch);
          });

          this.updateVariantVisualFromCard(card, Number(optionIndex));
          this.updateSelected();
          return;
        }

        const card = event.target.closest('[data-option-card]');
        if (card) {
          if (
            event.target.closest('[data-variant-controls]') ||
            event.target.closest('[data-variant-option]') ||
            event.target.closest('select') ||
            event.target.closest('button')
          ) {
            return;
          }

          const input = card.querySelector('[data-option-input]');
          if (!input) return;

          if (input.type === 'radio') {
            input.checked = true;
          } else {
            input.checked = !input.checked;
          }

          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      this.root.addEventListener('change', (event) => {
        const body = this.root.querySelector('.j2-configurator-body');
        const scrollTop = body ? body.scrollTop : 0;

        if (event.target.matches('[data-step-select]')) {
          this.goToStep(Number(event.target.value));
          return;
        }

        if (event.target.matches('select[data-variant-option]')) {
          const select = event.target;
          const card = select.closest('[data-option-card]');
          const input = card ? card.querySelector('[data-option-input]') : null;

        if (input) {
          input.checked = true;

          const stepIndex = Number(input.dataset.stepIndex);
          this.enforceDefaultMultipleStep(stepIndex, input);
        }

        this.updateVariantVisualFromCard(card, Number(select.dataset.optionIndex));
        this.updateSelected();
        }

        if (event.target.matches('[data-option-input]')) {
          const stepIndex = Number(event.target.dataset.stepIndex);

          this.enforceDefaultMultipleStep(stepIndex, event.target);
          this.updateSelected();
        }

        if (body) {
          requestAnimationFrame(() => {
            body.scrollTop = scrollTop;
          });
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        const imageZoomModal = this.root.querySelector('[data-image-zoom-modal]');

        if (imageZoomModal && !imageZoomModal.hidden) {
          this.closeImageZoom();
          return;
        }

        const modal = this.root.querySelector('[data-modal]');
        if (modal && !modal.hidden) {
          this.close();
        }
      });
    }

    zoomImageUrl(url) {
      if (!url) return '';

      try {
        const imageUrl = new URL(url, window.location.origin);

        // Normal configurator image is only 420px.
        // Only request the larger version after someone clicks zoom.
        imageUrl.searchParams.set('width', '1600');

        return imageUrl.toString();
      } catch (error) {
        return String(url).replace(/([?&])width=\d+/i, '$1width=1600');
      }
    }

    openImageZoom(button) {
      const card = button.closest('[data-option-card]');
      const sourceImage = card?.querySelector('[data-option-img]');
      const modal = this.root.querySelector('[data-image-zoom-modal]');
      const zoomImage = modal?.querySelector('[data-image-zoom-img]');

      if (!sourceImage || !modal || !zoomImage) return;

      this.imageZoomOpener = button;

      zoomImage.src = this.zoomImageUrl(
        sourceImage.currentSrc || sourceImage.src
      );

      zoomImage.alt = sourceImage.alt || '';

      modal.hidden = false;

      requestAnimationFrame(() => {
        modal.querySelector('[data-image-zoom-close]')?.focus();
      });
    }

    closeImageZoom() {
      const modal = this.root.querySelector('[data-image-zoom-modal]');
      const zoomImage = modal?.querySelector('[data-image-zoom-img]');

      if (!modal || modal.hidden) return;

      modal.hidden = true;

      if (zoomImage) {
        zoomImage.removeAttribute('src');
        zoomImage.alt = '';
      }

      if (this.imageZoomOpener?.isConnected) {
        this.imageZoomOpener.focus();
      }

      this.imageZoomOpener = null;
    }

    open() {
      const modal = this.root.querySelector('[data-modal]');
      if (!modal) return;

      this.applyDefaultSelections();
      this.resetVariantControls();
      this.goToStep(0);
      this.applyIncludedDuplicateRules();
      this.updateSelected();

      modal.hidden = false;
      document.documentElement.classList.add('j2-configurator-is-open');
    }

    close() {
      const modal = this.root.querySelector('[data-modal]');
      if (modal) modal.hidden = true;

      document.documentElement.classList.remove('j2-configurator-is-open');

      this.resetSelections();
    }

    resetSelections() {
      this.applyDefaultSelections();

      this.resetVariantControls();
      this.applyIncludedDuplicateRules();

      this.selected.clear();
      this.stepIndex = 0;

      this.goToStep(0);
      this.updateSelected();

      const submitButton = this.root.querySelector('[data-submit]');
      if (submitButton) {
        submitButton.removeAttribute('aria-disabled');
        submitButton.classList.remove('is-loading');
        submitButton.textContent = 'Toevoegen';
      }
    }

    resetVariantControls() {
      this.root.querySelectorAll('[data-option-card]').forEach((card) => {
        card.querySelectorAll('select[data-variant-option]').forEach((select) => {
          if (select.options.length) {
            select.selectedIndex = 0;
          }
        });

        const optionIndexes = new Set();

        card.querySelectorAll('button[data-variant-option]').forEach((button) => {
          optionIndexes.add(button.dataset.optionIndex);
        });

        optionIndexes.forEach((optionIndex) => {
          const buttons = [...card.querySelectorAll(`button[data-variant-option][data-option-index="${optionIndex}"]`)];

          buttons.forEach((button, index) => {
            button.classList.toggle('is-active', index === 0);
          });
        });

        this.updateVariantVisualFromCard(card);
      });
    }

    goToStep(nextIndex) {
      const previousIndex = this.stepIndex;

      this.stepIndex = Math.min(Math.max(nextIndex, 0), this.steps.length - 1);

      this.root.querySelectorAll('[data-step]').forEach((el) => {
        el.hidden = Number(el.dataset.step) !== this.stepIndex;
      });

      this.root.querySelectorAll('[data-progress-step]').forEach((el) => {
        el.classList.toggle('is-active', Number(el.dataset.progressStep) === this.stepIndex);
        el.classList.toggle('is-complete', Number(el.dataset.progressStep) < this.stepIndex);
      });

      const stepSelect = this.root.querySelector('[data-step-select]');
      if (stepSelect) stepSelect.value = String(this.stepIndex);

      const prevButton = this.root.querySelector('[data-prev]');
      const nextButton = this.root.querySelector('[data-next]');
      const submitButton = this.root.querySelector('[data-submit]');

      if (prevButton) prevButton.disabled = this.stepIndex === 0;
      if (nextButton) nextButton.hidden = this.stepIndex === this.steps.length - 1;
      if (submitButton) submitButton.hidden = this.stepIndex !== this.steps.length - 1;

      if (previousIndex !== this.stepIndex) {
        const body = this.root.querySelector('.j2-configurator-body');

        if (body) {
          requestAnimationFrame(() => {
            body.scrollTop = 0;
          });
        }
      }
    }

    variantsFor(item) {
      return Array.isArray(item.variants) ? item.variants : [];
    }

    optionNamesFor(item) {
      return Array.isArray(item.optionNames) ? item.optionNames : [];
    }

    isIncludedStep(step) {
      return step?.mode === 'included-default' || step?.key === 'inbegrepen';
    }

    itemKey(item) {
      const value = item?.matchKey || item?.linkedProductId || item?.productId || '';

      return String(value)
        .trim()
        .toLowerCase();
    }

    includedKeys() {
      const keys = new Set();

      this.steps.forEach((step) => {
        const items = Array.isArray(step.items) ? step.items : [];

        items.forEach((item) => {
          if (!this.isIncludedStep(step) && !item.included) return;

          const key = this.itemKey(item);

          if (key) {
            keys.add(key);
          }
        });
      });

      return keys;
    }

    selectedIncludedKeys() {
      const keys = new Set();

      this.root.querySelectorAll('[data-option-input]:checked').forEach((input) => {
        const stepIndex = Number(input.dataset.stepIndex);
        const itemIndex = Number(input.dataset.itemIndex);
        const step = this.steps?.[stepIndex];
        const item = step?.items?.[itemIndex];

        if (!item) return;
        if (!this.isIncludedStep(step) && !item.included) return;

        const key = this.itemKey(item);

        if (key) {
          keys.add(key);
        }
      });

      return keys;
    }

    isIncludedMatchItem(item) {
      const key = this.itemKey(item);

      if (!key) return false;

      return this.includedKeys().has(key) && !item.included;
    }

    applyIncludedDuplicateRules() {
      const allIncludedKeys = this.includedKeys();
      const selectedIncludedKeys = this.selectedIncludedKeys();

      this.root.querySelectorAll('[data-option-input]').forEach((input) => {
        const stepIndex = Number(input.dataset.stepIndex);
        const itemIndex = Number(input.dataset.itemIndex);
        const step = this.steps?.[stepIndex];
        const item = step?.items?.[itemIndex];
        const card = input.closest('[data-option-card]');

        if (!item || !card) return;

        const key = this.itemKey(item);
        const isOriginalIncluded = this.isIncludedStep(step) || item.included;
        const isIncludedMatch = Boolean(key && allIncludedKeys.has(key) && !isOriginalIncluded);
        const isAlreadySelectedAsIncluded = Boolean(key && selectedIncludedKeys.has(key));
        const disableBecauseAlreadyIncluded = isIncludedMatch && isAlreadySelectedAsIncluded;

        const message = card.querySelector('[data-rule-message]');
        const priceEl = card.querySelector('[data-option-price]');

        if (isIncludedMatch) {
          item.virtual = true;
          item.includedDuplicate = true;
          item.priceCents = 0;
          item.compareAtPriceCents = null;

          if (priceEl) {
            priceEl.innerHTML = `+ ${this.money(0)}`;
          }
        }

        card.classList.toggle('is-included-match', isIncludedMatch);
        card.classList.toggle('is-disabled-by-included', disableBecauseAlreadyIncluded);

        if (disableBecauseAlreadyIncluded) {
          input.checked = false;
          input.disabled = true;
          input.dataset.disabledByIncluded = 'true';

          card.querySelectorAll('select[data-variant-option], button[data-variant-option]').forEach((control) => {
            control.disabled = true;
            control.dataset.disabledByIncluded = 'true';
          });

          if (message) {
            message.dataset.ruleOwner = 'included';
            message.hidden = false;
            message.textContent = 'Al inbegrepen';
          }

          return;
        }

        if (input.dataset.disabledByIncluded === 'true') {
          input.disabled = false;
          delete input.dataset.disabledByIncluded;
        }

        card.querySelectorAll('select[data-variant-option], button[data-variant-option]').forEach((control) => {
          if (control.dataset.disabledByIncluded === 'true') {
            control.disabled = false;
            delete control.dataset.disabledByIncluded;
          }
        });

        if (isIncludedMatch) {
          if (message) {
            message.dataset.ruleOwner = 'included';
            message.hidden = false;
            message.textContent = 'Inbegrepen bij dit voertuig - gratis';
          }
        } else if (message?.dataset.ruleOwner === 'included') {
          message.hidden = true;
          message.textContent = '';
          delete message.dataset.ruleOwner;
        }
      });
    }

    virtualSelectedItem(item, variant = null) {
      const variantOptions = Array.isArray(variant?.options)
        ? variant.options.filter(Boolean)
        : [];

      const variantTitle = variantOptions.length
        ? variantOptions.join(' / ')
        : '';

      return {
        ...item,
        variantId: null,
        selectedVariantId: variant?.id ? Number(variant.id) : null,
        selectedVariantTitle: variantTitle,
        priceCents: 0,
        compareAtPriceCents: null,
        virtual: true,
        included: Boolean(item.included),
        includedDuplicate: Boolean(item.includedDuplicate),
        image: variant?.image || item.image || null,
        label: this.hasConfigurableVariants(item) && variantTitle
          ? `${item.label} - ${variantTitle}`
          : item.label
      };
    }

    isDefaultMultipleStep(step) {
      return step?.mode === 'default-multiple';
    }

    inputsForStep(stepIndex) {
      return [...this.root.querySelectorAll(`[data-option-input][data-step-index="${stepIndex}"]`)];
    }

    itemForInput(input) {
      const stepIndex = Number(input.dataset.stepIndex);
      const itemIndex = Number(input.dataset.itemIndex);

      return this.steps?.[stepIndex]?.items?.[itemIndex];
    }

    enforceDefaultMultipleStep(stepIndex, changedInput = null) {
      const step = this.steps?.[stepIndex];

      if (!this.isDefaultMultipleStep(step)) return;

      const inputs = this.inputsForStep(stepIndex);

      const defaultInput = inputs.find((input) => {
        const item = this.itemForInput(input);
        return Boolean(item?.selectedByDefault);
      });

      if (!defaultInput) return;

      const otherInputs = inputs.filter((input) => input !== defaultInput);

      // If the default option is clicked on, turn all others off.
      if (changedInput === defaultInput && defaultInput.checked) {
        otherInputs.forEach((input) => {
          input.checked = false;
        });
        return;
      }

      const hasOtherSelected = otherInputs.some((input) => input.checked);

      if (hasOtherSelected) {
        defaultInput.checked = false;
      } else {
        defaultInput.checked = true;
      }
    }

    updateVariantOptionAvailability(card) {
      if (!card) return;

      const input = card.querySelector('[data-option-input]');
      if (!input) return;
      if (input.disabled) return;

      const stepIndex = Number(input.dataset.stepIndex);
      const itemIndex = Number(input.dataset.itemIndex);
      const item = this.steps?.[stepIndex]?.items?.[itemIndex];

      if (!item || !this.hasConfigurableVariants(item)) return;

      const variants = this.variantsFor(item);
      const currentSelection = this.selectionFromCard(item, card);

      card.querySelectorAll('select[data-variant-option]').forEach((select) => {
        const optionIndex = Number(select.dataset.optionIndex);

        [...select.options].forEach((option) => {
          const originalLabel =
            option.dataset.originalLabel ||
            option.textContent
              .replace(/\s—\sNiet mogelijk$/i, '')
              .replace(/\s—\sNiet beschikbaar$/i, '');

          option.dataset.originalLabel = originalLabel;

          const testSelection = [...currentSelection];

          // Test this dropdown value while keeping the other dropdown choices.
          testSelection[optionIndex] = option.value;

          const possible = variants.some((variant) => {
            return testSelection.every((value, index) => {
              return String(variant.options?.[index]) === String(value);
            });
          });

          option.disabled = !possible;
          option.textContent = possible
            ? originalLabel
            : `${originalLabel} — Niet mogelijk`;
        });

        // If the currently selected option became impossible, move to first possible option.
        if (select.selectedOptions[0]?.disabled) {
          const firstPossible = [...select.options].find((option) => !option.disabled);

          if (firstPossible) {
            select.value = firstPossible.value;
          }
        }

        select.classList.toggle(
          'has-impossible-options',
          [...select.options].some((option) => option.disabled)
        );
      });
    }

    hasConfigurableVariants(item) {
      const variants = this.variantsFor(item);
      const optionNames = this.optionNamesFor(item);

      if (!variants.length) return false;
      if (variants.length <= 1) return false;
      if (item.hasOnlyDefaultVariant) return false;

      return optionNames.some((name, optionIndex) => {
        if (!name || name === 'Title') return false;

        const values = this.availableValuesForOption(item, optionIndex);

        return values.length > 1;
      });
    }

    availableValuesForOption(item, optionIndex) {
      const values = [];

      this.variantsFor(item).forEach((variant) => {
        const value = variant.options?.[optionIndex];

        if (value && !values.includes(value)) {
          values.push(value);
        }
      });

      return values;
    }

    initialSelectionFor(item) {
      const firstVariant = this.variantsFor(item)[0];

      if (!firstVariant || !Array.isArray(firstVariant.options)) {
        return [];
      }

      return [...firstVariant.options];
    }

    variantControlsMarkup(item, stepIndex, itemIndex) {
      if (!this.hasConfigurableVariants(item)) return '';

      const optionNames = this.optionNamesFor(item);
      const initialSelection = this.initialSelectionFor(item);

      return `
        <div class="j2-configurator-variant-controls" data-variant-controls>
          ${optionNames.map((optionName, optionIndex) => {
            const values = this.availableValuesForOption(item, optionIndex);
            const selectedValue = initialSelection[optionIndex] || values[0] || '';

            // Hide this selector if this option only has one possible value.
            // Example: only one color or only one size.
            if (!values.length || values.length <= 1) return '';

            return `
              <label class="j2-configurator-variant-group">
                <span class="j2-configurator-variant-label">${this.escape(optionName)}</span>

                <select
                  data-variant-option
                  data-option-index="${optionIndex}"
                  data-step-index="${stepIndex}"
                  data-item-index="${itemIndex}"
                >
                  ${values.map((value) => `
                    <option value="${this.escapeAttr(value)}"${value === selectedValue ? ' selected' : ''}>
                      ${this.escape(value)}
                    </option>
                  `).join('')}
                </select>
              </label>
            `;
          }).join('')}
        </div>
      `;
    }

    selectionFromCard(item, card) {
      const fallback = this.initialSelectionFor(item);

      if (!this.hasConfigurableVariants(item) || !card) {
        return fallback;
      }

      const selection = [...fallback];

      card.querySelectorAll('[data-variant-option]').forEach((control) => {
        const optionIndex = Number(control.dataset.optionIndex);

        if (control.tagName === 'SELECT') {
          selection[optionIndex] = control.value;
          return;
        }

        if (control.classList.contains('is-active')) {
          selection[optionIndex] = control.dataset.optionValue;
        }
      });

      return selection;
    }

    variantForSelection(item, selection, preferredOptionIndex = null) {
      const variants = this.variantsFor(item);

      if (!variants.length) {
        return {
          id: item.variantId ?? item.variant_id,
          title: item.variantTitle ?? item.variant_title ?? 'Default Title',
          options: [],
          priceCents: item.priceCents ?? item.price_cents ?? 0,
          compareAtPriceCents: item.compareAtPriceCents ?? item.compare_at_price_cents ?? null,
          image: item.image || null
        };
      }

      const exact = variants.find((variant) => {
        return selection.every((value, index) => {
          return String(variant.options?.[index]) === String(value);
        });
      });

      if (exact) return exact;

      if (preferredOptionIndex !== null && preferredOptionIndex >= 0) {
        const preferredValue = selection[preferredOptionIndex];

        const preferred = variants.find((variant) => {
          return String(variant.options?.[preferredOptionIndex]) === String(preferredValue);
        });

        if (preferred) return preferred;
      }

      const partial = variants.find((variant) => {
        return selection.some((value, index) => {
          return String(variant.options?.[index]) === String(value);
        });
      });

      return partial || variants[0];
    }

    syncCardControlsToVariant(card, variant) {
      if (!card || !variant || !Array.isArray(variant.options)) return;

      variant.options.forEach((value, optionIndex) => {
        const select = card.querySelector(`select[data-variant-option][data-option-index="${optionIndex}"]`);
        if (select) {
          select.value = value;
        }

        card.querySelectorAll(`button[data-variant-option][data-option-index="${optionIndex}"]`).forEach((button) => {
          button.classList.toggle('is-active', String(button.dataset.optionValue) === String(value));
        });
      });
    }

    priceMarkup(item, variant = null) {
      if (
        item?.virtual ||
        item?.included ||
        item?.includedDuplicate ||
        this.isIncludedMatchItem?.(item)
      ) {
        return `+ ${this.money(0)}`;
      }

      const selectedVariant = variant || this.variantForSelection(item, this.initialSelectionFor(item));

      const price = Number(
        selectedVariant.priceCents ??
        selectedVariant.price_cents ??
        item.priceCents ??
        item.price_cents ??
        0
      );

      const compareAt = Number(
        selectedVariant.compareAtPriceCents ??
        selectedVariant.compare_at_price_cents ??
        item.compareAtPriceCents ??
        item.compare_at_price_cents ??
        0
      );

      return `
        + ${this.money(price)}
        ${
          compareAt && compareAt > price
            ? `<s>${this.money(compareAt)}</s>`
            : ''
        }
      `;
    }

    selectedItemFor(item, variant) {
      const variantOptions = Array.isArray(variant.options) ? variant.options.filter(Boolean) : [];
      const variantTitle = variantOptions.length ? variantOptions.join(' / ') : '';

      return {
        ...item,
        variantId: Number(variant.id),
        priceCents: Number(variant.priceCents ?? variant.price_cents ?? 0),
        compareAtPriceCents: variant.compareAtPriceCents ?? variant.compare_at_price_cents ?? null,
        image: variant.image || item.image || null,
        variantTitle,
        label: this.hasConfigurableVariants(item) && variantTitle
          ? `${item.label} - ${variantTitle}`
          : item.label
      };
    }

    updateVariantVisualFromCard(card, preferredOptionIndex = null) {
      if (!card) return;

      const input = card.querySelector('[data-option-input]');
      if (!input) return;

      const stepIndex = Number(input.dataset.stepIndex);
      const itemIndex = Number(input.dataset.itemIndex);
      const item = this.steps?.[stepIndex]?.items?.[itemIndex];

      if (!item) return;

      const selection = this.selectionFromCard(item, card);
      const variant = this.variantForSelection(item, selection, preferredOptionIndex);

      this.syncCardControlsToVariant(card, variant);
      this.updateVariantOptionAvailability(card);

      const priceEl = card.querySelector('[data-option-price]');
      if (priceEl) {
        priceEl.innerHTML = this.priceMarkup(item, variant);
      }

      const imgEl = card.querySelector('[data-option-img]');
      if (imgEl && variant.image) {
        imgEl.src = variant.image;
      }
    }

    applyDefaultSelections() {
      this.root.querySelectorAll('[data-option-input]').forEach((input) => {
        const stepIndex = Number(input.dataset.stepIndex);
        const itemIndex = Number(input.dataset.itemIndex);
        const item = this.steps?.[stepIndex]?.items?.[itemIndex];

        input.checked = Boolean(item?.selectedByDefault);
      });
    }

    updateSelected() {
      this.applyIncludedDuplicateRules();

      this.steps.forEach((step, stepIndex) => {
        if (this.isDefaultMultipleStep?.(step)) {
          this.enforceDefaultMultipleStep(stepIndex);
        }
      });

      this.selected.clear();

      this.root.querySelectorAll('[data-option-input]:checked').forEach((input) => {
        const stepIndex = Number(input.dataset.stepIndex);
        const itemIndex = Number(input.dataset.itemIndex);
        const item = this.steps?.[stepIndex]?.items?.[itemIndex];

        if (!item) return;

        if (item.virtual || item.included || item.includedDuplicate || this.isIncludedMatchItem(item)) {
          const card = input.closest('[data-option-card]');
          const selection = this.selectionFromCard(item, card);
          const variant = this.hasConfigurableVariants(item)
            ? this.variantForSelection(item, selection)
            : null;

          this.selected.set(`${stepIndex}-${itemIndex}`, this.virtualSelectedItem(item, variant));
          return;
        }

        const card = input.closest('[data-option-card]');
        const selection = this.selectionFromCard(item, card);
        const variant = this.variantForSelection(item, selection);
        const selectedItem = this.selectedItemFor(item, variant);

        this.selected.set(`${stepIndex}-${itemIndex}`, selectedItem);
      });

      const selectedItems = [...this.selected.values()];
      const selectedTotal = selectedItems.reduce((sum, item) => {
        return sum + Number(item.priceCents ?? item.price_cents ?? 0);
      }, 0);

      const totalEl = this.root.querySelector('[data-selected-total]');
      if (totalEl) {
        totalEl.textContent = this.money(selectedTotal);
      }

      const countEl = this.root.querySelector('[data-selected-count]');
      if (countEl) {
        countEl.textContent = selectedItems.length;
      }
    }

    async submit() {
      const submitButton = this.root.querySelector('[data-submit]');
      const mainVariantId = this.mainVariantId;

      if (!mainVariantId) {
        alert('Kies eerst een voertuigvariant.');
        return;
      }

      if (submitButton.getAttribute('aria-disabled') === 'true') return;

      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('is-loading');
      submitButton.textContent = 'Toevoegen...';

      const selectedItems = [...this.selected.values()];
      const selectedLabels = selectedItems.map((item) => item.label).join(', ');

      const includedLabels = selectedItems
        .filter((item) => item.included || item.includedDuplicate)
        .map((item) => item.label)
        .join(', ');

      const purchasableItems = selectedItems.filter((item) => {
        return !item.virtual && item.variantId && !Number.isNaN(Number(item.variantId));
      });

      const selectedByStep = this.steps.map((step, stepIndex) => {
        const labels = [...this.selected.entries()]
          .filter(([key]) => key.startsWith(`${stepIndex}-`))
          .map(([, item]) => item.label);

        return labels.length ? `${step.title}: ${labels.join(', ')}` : null;
      }).filter(Boolean).join(' | ');

      const quantity = this.quantity;

      const items = [
        {
          id: mainVariantId,
          quantity,
          properties: {
            '_Configuratie': 'J2 voertuig configurator',
            '_Gekozen opties': selectedLabels || 'Geen extra opties',
            'Inbegrepen': includedLabels || 'Geen',
            '_Configuratie per stap': selectedByStep || 'Geen extra opties'
          }
        },
        ...purchasableItems.map((item) => ({
          id: Number(item.variantId),
          quantity,
          properties: {
            '_Hoort bij': this.data.productTitle || 'Voertuig',
            '_Configurator optie': item.label
          }
        }))
      ];

      const cartDrawer = document.querySelector('cart-drawer');

      let sections = 'cart-icon-bubble';

      if (cartDrawer) {
        const cartDrawerSection = cartDrawer.closest('.shopify-section');

        if (cartDrawerSection) {
          sections += `,${cartDrawerSection.id.replace('shopify-section-', '')}`;
        }
      }

      const cartAddUrl = window.theme?.routes?.cartAdd || `${window.Shopify?.routes?.root || '/'}cart/add.js`;

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname
          })
        });

        const data = await response.json().catch(() => null);

        if (!response.ok || data?.status) {
          throw new Error(
            data?.description ||
            data?.message ||
            'Kan configuratie niet toevoegen aan winkelwagen.'
          );
        }

        this.close();

        if (cartDrawer && data?.sections && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(data, true);
        } else {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
            bubbles: true
          }));

          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
            bubbles: true
          }));
        }
      } catch (error) {
        alert(error.message || 'Er ging iets mis. Probeer het opnieuw.');

        submitButton.removeAttribute('aria-disabled');
        submitButton.classList.remove('is-loading');
        submitButton.textContent = 'Toevoegen';
      }
    }

    money(cents) {
      const amount = Number(cents || 0) / 100;
      const currency = window.Shopify?.currency?.active || this.data.currency || 'EUR';
      const locale = document.documentElement.lang || 'nl-NL';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency
        }).format(amount);
      } catch (error) {
        return `€${amount.toFixed(2).replace('.', ',')}`;
      }
    }

    escape(value) {
      return String(value ?? '').replace(/[&<>"']/g, (char) => {
        return {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#039;'
        }[char];
      });
    }

    escapeAttr(value) {
      return this.escape(value);
    }
  }

  const instances = new Map();

  function initConfigurators(scope = document) {
    scope.querySelectorAll('[data-j2-configurator]').forEach((root) => {
      if (root.dataset.j2Initialized === 'true') return;

      root.dataset.j2Initialized = 'true';

      const instance = new J2VehicleConfigurator(root);
      instances.set(instance.sectionId, instance);
    });
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-j2-configurator-open]');
    if (!button) return;

    event.preventDefault();

    const sectionId = button.dataset.j2ConfiguratorOpen;
    const instance = instances.get(sectionId);

    if (instance) {
      instance.open();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initConfigurators());
  } else {
    initConfigurators();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initConfigurators(event.target);
  });
})();